import styles from "./reportsSummary.module.css";
import React, { useCallback, useState } from "react";
import i18n from "../../i18n";
import { tWithKeyAttr as t } from "../../i18nHelper";
import { skipToken } from "@reduxjs/toolkit/query";
import VenchiMessage from "../venchiMessage/venchiMessage";
import BackArrowButton from "../backArrowButton/backArrowButton";
import {
  useGetReportsByUserQuery,
  useSendMeetingReportByEmailMutation,
} from "../../services/api/user.api";
import { Report } from "../../types/types";
import {
  IReportsSummaryProps,
  isActiveReport,
} from "../../services/types/reports.types";
import pdfIcon from "../../images/pdf_icon.svg";
import Modal from "../modals/modal/Modal";
import GeneralSuccessPopup from "../modals/generalSuccessPopup/generalSuccessPopup";
import { openAuthenticatedFile, downloadAuthenticatedFile } from "../../utils/fileDownload";

function ReportsSummary({ user, onClose }: IReportsSummaryProps) {
  const {
    data,
    isSuccess: isReportsLoaded,
    error: reportsError,
  } = useGetReportsByUserQuery(user.Users.Guid || skipToken);

  const getReportTypeLabel = (type: number) => {
    switch (type) {
      case 1:
        return t("REPORT_TYPE_SUMMARY", "סיכום טיפול");
      case 2:
        return t("REPORT_TYPE_QUESTIONNAIRE", "שאלון");
      case 3:
        return t("REPORT_TYPE_THERAPIST_ACTION", "דוח פעולה");
      default:
        return t("UNKNOWN", "לא ידוע");
    }
  };

  //need to add loading indicator
  const [sendReport, { isLoading: isEmailLoading }] =
    useSendMeetingReportByEmailMutation();

  const [openModalSuccess, setOpenModalSuccess] = useState(false);

  const canSendEmailToUser = user.Users?.Email;
  const handleDownloadClick = useCallback(
    (fileUrl: string, reportId: number): void => {
      downloadAuthenticatedFile(fileUrl, t("REPORT_FILENAME_PREFIX", { reportId }))
        .catch((error) => console.error(t("DOWNLOAD_ERROR"), error));
    },
    []
  );

  const viewReport = useCallback((fileUrl: string): void => {
    openAuthenticatedFile(fileUrl)
      .catch((error) => console.error(t("DOWNLOAD_ERROR"), error));
  }, []);

  // Type-safe email handler
  const handleEmailClick = (id: number): void => {
    canSendEmailToUser &&
      sendReport({ reportId: id, email: canSendEmailToUser }).then((res) => {
        if ("data" in res && res.data.success) {
          setOpenModalSuccess(true);
        }
      });
  };

  // Early return for no reports with proper typing
  if (data?.length === 0) {
    return (
      <section className={styles.section} dir={i18n.dir()}>
        <BackArrowButton
          text={t("PREVIOUS_SCREEN")}
          onClick={onClose}
          position={"center"}
        />
        <VenchiMessage message={t("NO_REPORTS_MESSAGE")} />
      </section>
    );
  }

  // Early return for loading state
  if (!isReportsLoaded) {
    return (
      <section className={styles.section} dir={i18n.dir()}>
        <BackArrowButton
          text={t("BACK_BUTTON")}
          onClick={onClose}
          position={"center"}
        />
        <div>{t("LOADING_REPORTS")}</div>
      </section>
    );
  }

  // Early return for error state
  if (reportsError) {
    const errorMessage = (() => {
      // Type assertion to handle RTK Query error types
      const error = reportsError as any;

      if (error?.data) {
        return String(error.data);
      }
      if (error?.message) {
        return String(error.message);
      }
      if (error?.status) {
        return t("HTTP_ERROR_LOADING_REPORTS", { status: error.status });
      }
      return t("UNKNOWN_ERROR_LOADING_REPORTS");
    })();

    return (
      <section className={styles.section} dir={i18n.dir()}>
        <BackArrowButton
          text={t("BACK_BUTTON")}
          onClick={onClose}
          position={"center"}
        />
        <div>
          {t("ERROR_LOADING_REPORTS")} {errorMessage}
        </div>
      </section>
    );
  }

  return (
    <section className={styles.section} dir={i18n.dir()}>
      <BackArrowButton
        text={t("PREVIOUS_SCREEN")}
        onClick={onClose}
        position={"center"}
      />
      <article className="section">
        <h4 className="sectionTitle">
          {t("REPORTS_FOR_USER_TITLE", {
            firstName: user?.Users?.FirstName || "",
            lastName: user?.Users?.LastName || "",
          })}{" "}
          - {t("BETA_VERSION")}
        </h4>
        <ul className={`list`}>
          <li
            className={`listRow listRow_header ${styles.listRow}`}
            // style={{ direction: i18n.dir() }}
          >
            <p className="listCell">{t("INQUIRY_ID")}</p>
            <p className="listCell">{t("INQUIRY_NAME_HEADER")}</p>
            <p className="listCell">{t("INQUIRY_TYPE_HEADER", "סוג דוח")}</p>
            <p className="listCell">{t("INQUIRY_DATE_HEADER")}</p>
            <p className="listCell">{t("INQUIRY_ACTIONS_HEADER")}</p>
            <p className="listCell">{t("INQUIRY_PREVIEW_HEADER")}</p>
          </li>

          {data &&
            data.map((item: Report, index) => {
              if (isActiveReport(item)) {
                return (
                  <li
                    className={`listRow ${styles.listRow} ${
                      index % 2 === 0 ? "listRow_colored" : ""
                    } `}
                    // className={styles.inquiry}
                    key={item.id}
                    onClick={() => {
                      // Don't prevent default here, let child elements handle their own clicks
                    }}
                  >
                    <p className="listCell">{item.id}</p>
                    <p className="listCell">{item.createdBy}</p>
                    <p className="listCell">{getReportTypeLabel(item.type)}</p>
                    <p className="listCell">
                      {new Date(item.createdOn).toLocaleDateString(
                        t("HEBREW_LOCALE")
                      )}
                    </p>

                    <div className="listCell">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        onClick={() =>
                          handleDownloadClick(item.fileUrl, item.id)
                        }
                        style={{ cursor: "pointer" }}
                        role="button"
                        tabIndex={0}
                        aria-label={t("DOWNLOAD_REPORT")}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleDownloadClick(item.fileUrl, item.id);
                          }
                        }}
                      >
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="7 10 12 15 17 10" />
                        <line x1="12" y1="15" x2="12" y2="3" />
                      </svg>

                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{ marginInlineStart: "8px", cursor: "pointer" }}
                        onClick={() => handleEmailClick(item.id)}
                        role="button"
                        tabIndex={0}
                        aria-label={t("SEND_EMAIL")}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleEmailClick(item.id);
                          }
                        }}
                      >
                        <path
                          d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4
                            c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"
                        />
                        <polyline points="22,6 12,13 2,6" />
                      </svg>
                    </div>

                    <div className="listCell">
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          viewReport(item.fileUrl);
                        }}
                        style={{ cursor: "pointer" }}
                        role="button"
                        tabIndex={0}
                        aria-label={t("VIEW_PDF")}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            viewReport(item.fileUrl);
                          }
                        }}
                      >
                        <img
                          src={pdfIcon}
                          alt={t("VIEW_PDF")}
                          width="24"
                          height="24"
                        />
                      </span>
                    </div>
                  </li>
                );
              }
              return null;
            })}
        </ul>
      </article>
      {openModalSuccess && (
        <Modal onClose={() => setOpenModalSuccess(false)}>
          <GeneralSuccessPopup
            onClose={() => {
              setOpenModalSuccess(false);
            }}
          />
        </Modal>
      )}
    </section>
  );
}

export default ReportsSummary;
